// The Document part's server state (/api/me/pages): every query key and
// mutation of the part lives here.
//
// Promise:
// - Keys start with ["me", "documents", ...]; each has a prefix next to it for
//   invalidation. The detail is keyed by what the URL names (the 12-character
//   key); realtime events name documents by id, so `invalidateDetailsOf` finds
//   the cached details of an id by their data.
// - Renaming, archiving, deleting and creating show at once and roll back when
//   the server refuses; everything is fetched again when it settles. Copy and
//   restore wait for the server (the result is a new document / a new version).
import {
  infiniteQueryOptions,
  keepPreviousData,
  type QueryClient,
  type QueryKey,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  PageDetailDTO,
  PageDTO,
  PageListItemDTO,
  PageVersionDetailDTO,
  PageVersionDTO,
  UiAnswerDTO,
} from "@trameria/core/documents-shared";
import { useApi } from "../app/context";
import { useMeWorkspace } from "../app/workspace-filter";
import { type Api, idempotencyKeyFor } from "../data/api";
import { newOptimisticId } from "../data/optimistic";

export const documentsPrefix = ["me", "documents"] as const;

// ---- list -------------------------------------------------------------------

export interface PageListPage {
  pages: PageListItemDTO[];
  nextCursor: string | null;
}

export const listPrefix = [...documentsPrefix, "list"] as const;
export const listKey = (archived: boolean) =>
  [...listPrefix, archived ? "archived" : "active"] as const;

/**
 * The owner's documents, the latest change first, a page at a time; with a
 * typed word, those whose title holds it; narrowed to a Workspace (its id),
 * those of it (under the plain list's key, so the
 * writes that rewrite the plain list leave a narrowed one to be read again).
 */
export const listQuery = (
  api: Api,
  archived: boolean,
  words?: string,
  workspace: string | null = null,
) =>
  infiniteQueryOptions({
    queryKey:
      words || workspace
        ? ([...listKey(archived), workspace ?? "", words ?? ""] as const)
        : listKey(archived),
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams();
      if (archived) q.set("archived", "1");
      if (words) q.set("q", words);
      if (workspace) q.set("workspace", workspace);
      if (pageParam) q.set("before", pageParam);
      const qs = q.toString();
      return api<PageListPage>(`/api/me/pages${qs ? `?${qs}` : ""}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });

type ListData = { pages: PageListPage[]; pageParams: unknown[] };

/** The list with `fn` applied to its rows (pages of the infinite query). */
function mapRows(
  old: ListData | undefined,
  fn: (rows: PageListItemDTO[]) => PageListItemDTO[],
): ListData | undefined {
  if (!old) return old;
  return {
    ...old,
    pages: old.pages.map((p) => ({ ...p, pages: fn(p.pages) })),
  };
}

// ---- link candidates (`[[`) ---------------------------------------------------

export interface LinkCandidate {
  id: string;
  title: string;
}

/**
 * The documents `[[query` may link to: with no query the latest changed, with
 * one the documents whose title or text has it (titles first).
 */
export const linkCandidatesQuery = (api: Api, query: string) =>
  queryOptions({
    queryKey: [...documentsPrefix, "link-candidates", query] as const,
    queryFn: async (): Promise<LinkCandidate[]> => {
      const q = query.trim();
      if (q === "") {
        const page = await api<PageListPage>("/api/me/pages?limit=20");
        return page.pages.map((p) => ({ id: p.id, title: p.title }));
      }
      const res = await api<{
        results: Record<string, Array<{ id: string; label: string }>>;
      }>(`/api/search?types=page&limit=20&q=${encodeURIComponent(q)}`);
      const hits = (res.results.page ?? []).map((h) => ({
        id: h.id,
        title: h.label,
      }));
      return titlesFirst(hits, q);
    },
    staleTime: 30_000,
  });

/** Those whose title has `query` first (the rest matched by their text). */
export function titlesFirst(
  candidates: LinkCandidate[],
  query: string,
): LinkCandidate[] {
  const lower = query.trim().toLowerCase();
  const inTitle = (c: LinkCandidate) =>
    c.title.toLowerCase().includes(lower) ? 0 : 1;
  return [...candidates].sort((a, b) => inTitle(a) - inTitle(b));
}

/**
 * The owner's own documents for what was typed (title or text, titles first):
 * what an index of Me may be given. Search would bring the workspaces'
 * documents too, which Me's index does not take.
 */
export const ownCandidatesQuery = (api: Api, query: string) =>
  queryOptions({
    queryKey: [...documentsPrefix, "own-candidates", query] as const,
    queryFn: async (): Promise<LinkCandidate[]> => {
      const q = new URLSearchParams({ limit: "20" });
      if (query.trim()) {
        q.set("q", query.trim());
        q.set("body", "1");
      }
      const page = await api<PageListPage>(`/api/me/pages?${q}`);
      return titlesFirst(
        page.pages.map((p) => ({ id: p.id, title: p.title })),
        query,
      );
    },
    staleTime: 30_000,
  });

// ---- detail -----------------------------------------------------------------

export const detailPrefix = [...documentsPrefix, "detail"] as const;
export const detailKey = (key: string) => [...detailPrefix, key] as const;

/** One document with its body and live name, by key (or id: the API takes both). */
export const detailQuery = (api: Api, key: string) =>
  queryOptions({
    queryKey: detailKey(key),
    queryFn: () =>
      api<PageDetailDTO>(`/api/me/pages/${encodeURIComponent(key)}`),
  });

/** The cached detail queries of a document id (realtime events carry ids). */
function detailKeysOf(qc: QueryClient, id: string): QueryKey[] {
  return qc
    .getQueryCache()
    .findAll({ queryKey: detailPrefix })
    .filter((q) => (q.state.data as PageDetailDTO | undefined)?.page.id === id)
    .map((q) => q.queryKey);
}

export function invalidateDetailsOf(qc: QueryClient, id: string) {
  for (const queryKey of detailKeysOf(qc, id))
    void qc.invalidateQueries({ queryKey, exact: true });
}

/**
 * A new revision of a document. A markdown body arrives through live editing,
 * so only the revision is written and the detail is marked stale for the next
 * time it is opened; an html / slide body is fetched again (its frame shows the
 * stored body).
 */
export function noteRevision(qc: QueryClient, id: string, revision: number) {
  for (const queryKey of detailKeysOf(qc, id)) {
    const data = qc.getQueryData<PageDetailDTO>(queryKey);
    if (data?.page.kind === "markdown") {
      if (revision > data.revision)
        qc.setQueryData<PageDetailDTO>(queryKey, { ...data, revision });
      void qc.invalidateQueries({ queryKey, exact: true, refetchType: "none" });
    } else void qc.invalidateQueries({ queryKey, exact: true });
  }
}

// ---- versions ---------------------------------------------------------------

export const versionsPrefix = (pageId: string) =>
  [...documentsPrefix, "versions", pageId] as const;

export interface VersionsPage {
  versions: PageVersionDTO[];
  nextCursor: number | null;
}

export const versionsQuery = (api: Api, pageId: string) =>
  infiniteQueryOptions({
    queryKey: versionsPrefix(pageId),
    queryFn: ({ pageParam }) =>
      api<VersionsPage>(
        `/api/me/pages/${pageId}/versions${pageParam !== null ? `?before=${pageParam}` : ""}`,
      ),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
  });

/**
 * One version and the version it is compared with (`base`: the next older item
 * of the list; null lets the server take the row before it). A version never
 * changes once written under its revision, so it is never fetched again.
 */
export const versionQuery = (
  api: Api,
  pageId: string,
  revision: number,
  base: number | null,
) =>
  queryOptions({
    queryKey: [...documentsPrefix, "version", pageId, revision, base] as const,
    queryFn: () =>
      api<PageVersionDetailDTO>(
        `/api/me/pages/${pageId}/versions/${revision}${base === null ? "" : `?base=${base}`}`,
      ),
    staleTime: Number.POSITIVE_INFINITY,
  });

// ---- mutations --------------------------------------------------------------

/** Settles a change of a document: its details and both lists are fetched again. */
function settle(qc: QueryClient, id: string) {
  invalidateDetailsOf(qc, id);
  void qc.invalidateQueries({ queryKey: listPrefix });
}

/** Writes `change` into every cached copy of a document; returns how to undo it. */
function patchEverywhere(
  qc: QueryClient,
  id: string,
  change: {
    detail(d: PageDetailDTO): PageDetailDTO;
    lists(rows: PageListItemDTO[], archived: boolean): PageListItemDTO[];
  },
): () => void {
  const saved: Array<[QueryKey, unknown]> = [];
  for (const queryKey of detailKeysOf(qc, id)) {
    const before = qc.getQueryData<PageDetailDTO>(queryKey);
    saved.push([queryKey, before]);
    if (before) qc.setQueryData(queryKey, change.detail(before));
  }
  for (const archived of [false, true]) {
    const queryKey = listKey(archived);
    const before = qc.getQueryData<ListData>(queryKey);
    saved.push([queryKey, before]);
    qc.setQueryData<ListData>(queryKey, (old) =>
      mapRows(old, (rows) => change.lists(rows, archived)),
    );
  }
  return () => {
    for (const [queryKey, value] of saved) qc.setQueryData(queryKey, value);
  };
}

async function cancelAll(qc: QueryClient) {
  await qc.cancelQueries({ queryKey: documentsPrefix });
}

export interface CreateInput {
  title: string;
  /**
   * Made in the same place as this document (an id or key): one's own beside a
   * personal document, its workspace's space beside a shared one.
   */
  beside?: string;
}

export function useCreateDocument() {
  const api = useApi();
  const qc = useQueryClient();
  // A document made while Me is narrowed to a Workspace belongs to it.
  const [workspace] = useMeWorkspace();
  return useMutation({
    mutationFn: (input: CreateInput) =>
      api<{ page: PageDTO }>("/api/me/pages", {
        method: "POST",
        body:
          input.beside !== undefined
            ? { title: input.title, beside: input.beside }
            : workspace
              ? { title: input.title, workspace }
              : { title: input.title },
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onMutate: async (input) => {
      // Beside another document it may not be one's own: not shown in the list
      // before the server says where it went.
      if (input.beside !== undefined) return { undo: () => {} };
      await cancelAll(qc);
      const queryKey = listKey(false);
      const before = qc.getQueryData<ListData>(queryKey);
      const now = new Date().toISOString();
      qc.setQueryData<ListData>(queryKey, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((p, i) =>
                i === 0
                  ? {
                      ...p,
                      pages: [
                        {
                          id: newOptimisticId(),
                          key: "",
                          title: input.title,
                          kind: "markdown",
                          rawQuery: null,
                          workspaceId: workspace,
                          ownerUserId: null,
                          archivedAt: null,
                          createdAt: now,
                          updatedAt: now,
                          revision: 0,
                        },
                        ...p.pages,
                      ],
                    }
                  : p,
              ),
            }
          : old,
      );
      return { undo: () => qc.setQueryData(queryKey, before) };
    },
    onError: (_e, _i, context) => context?.undo(),
    onSettled: () => void qc.invalidateQueries({ queryKey: listPrefix }),
  });
}

export function useRenameDocument() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; title: string }) =>
      api<{ page: PageDTO }>(`/api/me/pages/${input.id}`, {
        method: "PATCH",
        body: { title: input.title },
      }),
    onMutate: async (input) => {
      await cancelAll(qc);
      const undo = patchEverywhere(qc, input.id, {
        detail: (d) => ({ ...d, page: { ...d.page, title: input.title } }),
        lists: (rows) =>
          rows.map((r) =>
            r.id === input.id ? { ...r, title: input.title } : r,
          ),
      });
      return { undo };
    },
    onError: (_e, _i, context) => context?.undo(),
    onSettled: (_d, _e, input) => settle(qc, input.id),
  });
}

export function useArchiveDocument() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { page: PageDTO; archived: boolean }) =>
      api<{ page: PageDTO }>(`/api/me/pages/${input.page.id}`, {
        method: "PATCH",
        body: { archived: input.archived },
      }),
    onMutate: async (input) => {
      await cancelAll(qc);
      const archivedAt = input.archived ? new Date().toISOString() : null;
      const undo = patchEverywhere(qc, input.page.id, {
        detail: (d) => ({
          ...d,
          page: { ...d.page, archivedAt },
          readOnly: input.archived
            ? "archived"
            : d.readOnly === "archived"
              ? null
              : d.readOnly,
          live: input.archived ? null : d.live,
        }),
        // Leaves the list it was in; the other list gets it when fetched again.
        lists: (rows, archived) =>
          archived === input.archived
            ? rows
            : rows.filter((r) => r.id !== input.page.id),
      });
      return { undo };
    },
    onError: (_e, _i, context) => context?.undo(),
    onSettled: (_d, _e, input) => settle(qc, input.page.id),
  });
}

export function useDeleteDocument() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { page: PageDTO }) =>
      api(`/api/me/pages/${input.page.id}`, { method: "DELETE" }),
    onMutate: async (input) => {
      await cancelAll(qc);
      const undo = patchEverywhere(qc, input.page.id, {
        detail: (d) => d,
        lists: (rows) => rows.filter((r) => r.id !== input.page.id),
      });
      return { undo };
    },
    onError: (_e, _i, context) => context?.undo(),
    onSettled: () => void qc.invalidateQueries({ queryKey: listPrefix }),
  });
}

export function useCopyDocument() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { page: PageDTO }) =>
      api<{ page: PageDTO }>(`/api/me/pages/${input.page.id}/copy`, {
        method: "POST",
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSettled: () => void qc.invalidateQueries({ queryKey: listPrefix }),
  });
}

export function useRestoreVersion(pageId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (revision: number) =>
      api<{ revision: number }>(
        `/api/me/pages/${pageId}/versions/${revision}/restore`,
        { method: "POST" },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: versionsPrefix(pageId) });
      settle(qc, pageId);
    },
  });
}

/** Saves the source of an html / slide document (the whole text, against a revision). */
export function useWriteHtml(pageId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { baseRevision: number; html: string }) =>
      api<{ revision: number }>(`/api/me/pages/${pageId}/body`, {
        method: "PUT",
        // A person who empties the source and saves means it.
        body: { ...input, allowEmpty: true },
      }),
    onSettled: () => settle(qc, pageId),
  });
}

export function useSetRawQuery(pageId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rawQuery: string | null) =>
      api<{ page: PageDTO }>(`/api/me/pages/${pageId}`, {
        method: "PATCH",
        body: { rawQuery },
      }),
    onSettled: () => settle(qc, pageId),
  });
}

// ---- ui answers (`useAnswers` of a body's ```ui blocks) ----------------------

/** All blocks' answers of a document, in one query (events name the document). */
export const uiAnswersKey = (pageId: string) =>
  [...documentsPrefix, "ui-answers", pageId] as const;

export const uiAnswersQuery = (api: Api, pageId: string) =>
  queryOptions({
    queryKey: uiAnswersKey(pageId),
    queryFn: async () =>
      (
        await api<{ answers: UiAnswerDTO[] }>(
          `/api/me/pages/${pageId}/ui-answers`,
        )
      ).answers,
  });

/** The files bound to a document (what its ```ui blocks may show). */
export const pageFilesKey = (pageId: string) =>
  [...documentsPrefix, "files", pageId] as const;

export const pageFilesQuery = (api: Api, pageId: string) =>
  queryOptions({
    queryKey: pageFilesKey(pageId),
    queryFn: async () =>
      (await api<{ fileIds: string[] }>(`/api/me/pages/${pageId}/files`))
        .fileIds,
  });

/** What one write of the viewer's own answer does. */
export type UiAnswerWrite =
  | { op: "save"; blockId: string; value: unknown }
  | { op: "clear"; blockId: string };

/** The writes of a document's answers (pending ones are found by this key). */
export const uiAnswerWriteKey = (pageId: string) =>
  [...documentsPrefix, "ui-answer-write", pageId] as const;

/**
 * Saves or withdraws the viewer's own answer. Nothing is written to the cache:
 * the pending write is laid over the fetched answers while it is in flight
 * (ui-answers.ts), so a refetch caused by someone else's answer meanwhile
 * does not drop it, and a failure just removes the overlay. Settling returns
 * the refetch, so the write stays pending until the real answers arrived.
 */
export function useUiAnswerWrite(pageId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: uiAnswerWriteKey(pageId),
    mutationFn: (write: UiAnswerWrite) =>
      api(
        `/api/me/pages/${pageId}/ui-answers/${encodeURIComponent(write.blockId)}`,
        write.op === "save"
          ? { method: "PUT", body: { value: write.value } }
          : { method: "DELETE" },
      ),
    onSettled: () =>
      qc.invalidateQueries({
        queryKey: uiAnswersKey(pageId),
        refetchType: "all",
      }),
  });
}

/** Fetches a detail again now (after a refused live connection or a 409). */
export function refetchDetail(qc: QueryClient, api: Api, key: string) {
  return qc.fetchQuery({ ...detailQuery(api, key), staleTime: 0 });
}
